<template>
  <v-row class="filters__dates" no-gutters>
    <v-col class="pr-1">
      <v-menu v-model="menuA" :close-on-content-click="closeOnClick" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :label="$t('filters.date.startDate')"
            @click:clear="handleClearStartEndDate()"
            v-model="startDateShow"
            v-bind="attrs"
            v-on="on"
            clearable
            outlined
            readonly
            dense
          />
        </template>
        <v-date-picker
          @change="handleStartDateSelected"
          v-model="startDateSelect"
          :min="minDate"
          color="primary"
          no-title
          :locale="localeLanguage"
          flat
        />
      </v-menu>
    </v-col>

    <v-col class="pl-1">
      <v-menu v-model="menuB" :close-on-content-click="closeOnClick" offset-y>
        <template v-slot:activator="{ on, attrs }">
          <v-text-field
            :label="$t('filters.date.endDate')"
            :disabled="isSecondDatePickerDisabled"
            @click:clear="handleClearEndDate()"
            v-model="endDateShow"
            v-bind="attrs"
            v-on="on"
            clearable
            outlined
            readonly
            dense
          />
        </template>
        <v-date-picker
          @change="handleEndDateSelected"
          v-model="endDateSelect"
          :min="minDatePicker"
          :max="maxDate"
          color="primary"
          :locale="localeLanguage"
          no-title
          flat
        />
      </v-menu>
    </v-col>
  </v-row>
</template>

<script>
import { mapGetters, mapMutations } from 'vuex'
import { catalogDateWithoutTime } from '@/util/catalog'
import store from '@/store'
import moment from 'moment'

import { dateOptions } from './FilterDatesOptions'

export default {
  name: 'FilterDatesRange',

  props: {
    fetchData: {
      type: Function,
      required: true,
    },

    startDateProp: {
      type: String,
    },

    endDateProp: {
      type: String,
    },

    minDate: {
      default: '',
      required: false,
    },

    maxDate: {
      default: '',
      required: false,
    },

    customStartDate: {
      default: false,
    },

    closeOnClick: {
      default: false
    },

    /**
     * Só busca quando o intervalo está completo. Opt-in porque muda o comportamento de todas as
     * telas que usam este filtro.
     *
     * Sem isso, escolher a data inicial já dispara uma busca com a final vazia - e a API completa a
     * final com o fim do MÊS ATUAL. Escolher 01/07/2025 vira "de julho de 2025 até hoje", que na
     * tela de notas são 42 mil pagamentos. Pior: essa busca gigante é lenta e chega DEPOIS da busca
     * certa (disparada ao escolher a data final), sobrescrevendo a lista com o intervalo errado.
     */
    requireFullRange: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      date: catalogDateWithoutTime,

      startDateSelect: null,
      endDateSelect: null,

      startDateShow: null,
      endDateShow: null,

      minDatePicker: '',
      isSecondDatePickerDisabled: true,

      params: {},

      menuA: false,
      menuB: false,
    }
  },

  computed: {
    ...mapGetters(['filters']),

    localeLanguage() {
      return localStorage.getItem('user_language') ?? 'pt-BR'
    }
  },

  methods: {
    ...mapMutations(['setFilters', 'setDatesValue', 'setCurrentPage']),

    handleStartDateSelected() {
      this.params[this.startDateProp] = `${this.startDateSelect}T00:00:01Z`
      this.isSecondDatePickerDisabled = false
      this.minDatePicker = this.startDateSelect

      if (!this.endDateSelect) {
        this.setDatesValue({
          ...store.getters.datesValue,
          ...{
            [this.startDateProp]: moment(this.startDateSelect).format(
              'DD/MM/YYYY'
            ),
          },
        })
      } else {
        this.setDatesValue({
          ...store.getters.datesValue,
          ...{
            [this.startDateProp]: `${moment(this.startDateSelect).format(
              'DD/MM/YYYY'
            )} até ${moment(this.endDateSelect).format('DD/MM/YYYY')}`,
          },
        })
      }

      this.startDateShow = moment(this.startDateSelect).format('DD/MM/YYYY')

      // Com requireFullRange, quem dispara é a data final; aqui só espera o intervalo fechar.
      if (this.requireFullRange && !this.endDateSelect) return

      this.applyFilter()
    },

    handleEndDateSelected() {
      this.params[this.endDateProp] = `${this.endDateSelect}T23:59:59Z`

      this.setDatesValue({
        ...store.getters.datesValue,
        ...{
          [this.startDateProp]: `${moment(this.startDateSelect).format(
            'DD/MM/YYYY'
          )} até ${moment(this.endDateSelect).format('DD/MM/YYYY')}`,
        },
      })

      this.endDateShow = moment(this.endDateSelect).format('DD/MM/YYYY')

      this.applyFilter()
    },

    handleClearEndDate() {
      this.endDateSelect = ''
      this.params[this.endDateProp] = ''

      this.handleStartDateSelected()
    },

    handleClearStartEndDate() {
      if (this.customStartDate) return

      this.endDateShow = ''
      this.endDateSelect = ''
      this.isSecondDatePickerDisabled = true

      if (!this.customStartDate) this.params[this.startDateProp] = ''
      this.params[this.endDateProp] = ''

      this.setDatesValue({
        ...store.getters.datesValue,
        ...{
          [this.startDateProp]: this.$t(
            `filters.date.${[dateOptions[0].title]}`
          ),
        },
      })

      this.applyFilter()
    },

    applyFilter() {
      this.isMenuOpened = false

      Promise.resolve(this.setFilters({ ...store.getters.filters, ...this.params }))
        .then(() => this.setCurrentPage(1))
        .then(() => this.fetchData())
    },
  },
}
</script>
